import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  RadioGroup,
  Radio,
  FormLabel
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  People,
  Settings,
  Business,
  Email,
  PersonAdd,
  Check,
  Cancel,
  MoreVert,
  Warning,
  Security,
  ExpandMore,
  Visibility,
  Inventory,
  CalendarMonth,
  Description,
  Assessment,
  Block,
  CheckCircle,
  Save,
  Policy
} from '@mui/icons-material';
import { UseApi } from '../Hooks/UseApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://inkedupmanager.com/api';

const StudioManagement = () => {
  const api = UseApi();
  const [loading, setLoading] = useState(true);
  const [studioData, setStudioData] = useState(null);
  const [members, setMembers] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // ADDED: Studio settings states
  const [studioSettings, setStudioSettings] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    client_ownership_policy: 'studio'  // NEW: Client ownership policy
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(false);
  
  // Invite member states
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'artist',
    first_name: '',
    last_name: ''
  });
  
  // Add seats states
  const [seatsDialog, setSeatsDialog] = useState(false);
  const [seatsToAdd, setSeatsToAdd] = useState(1);
  const [seatsLoading, setSeatsLoading] = useState(false);

  // Confirmation dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    memberToRemove: null
  });

  // Permission management states
  const [permissionDialog, setPermissionDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [permissionLoading, setPermissionLoading] = useState(false);

  const roleOptions = [
    { value: 'artist', label: 'Tattoo Artist' },
    { value: 'piercer', label: 'Piercer' },
    { value: 'admin', label: 'Studio Admin' },
    { value: 'receptionist', label: 'Receptionist' },
    { value: 'member', label: 'General Member' }
  ];

  // NEW: Client ownership policy options
  const clientOwnershipOptions = [
    { 
      value: 'studio', 
      label: 'All clients belong to studio',
      description: 'When a team member leaves, their clients are transferred to the studio owner. The departing member loses access to client data.'
    },
    { 
      value: 'artist', 
      label: 'Clients belong to creating artist',
      description: 'When a team member leaves, they keep their client data. The studio loses access to those clients.'
    },
    { 
      value: 'shared', 
      label: 'Shared - both retain access',
      description: 'When a team member leaves, both the studio and the departing member retain access to client data.'
    }
  ];

  // Permission groups (unchanged)
  const permissionGroups = [
    {
      title: 'Client Management',
      icon: <People sx={{ fontSize: 20 }} />,
      permissions: [
        {
          key: 'can_view_all_clients',
          label: 'View All Studio Clients',
          description: 'Access to see all clients from studio members'
        },
        {
          key: 'can_edit_all_clients',
          label: 'Edit All Studio Clients',
          description: 'Modify client information from all studio members'
        },
        {
          key: 'can_delete_all_clients',
          label: 'Delete Studio Clients',
          description: 'Remove clients from the studio database'
        },
        {
          key: 'can_export_client_data',
          label: 'Export Client Data',
          description: 'Download client information and reports'
        }
      ]
    },
    {
      title: 'Calendar & Appointments',
      icon: <CalendarMonth sx={{ fontSize: 20 }} />,
      permissions: [
        {
          key: 'can_view_calendars',
          label: 'View Studio Calendars',
          description: 'See all studio members\' calendars and appointments'
        },
        {
          key: 'can_manage_appointments',
          label: 'Manage Appointments',
          description: 'Create, edit, and delete appointments'
        },
        {
          key: 'can_view_all_appointments',
          label: 'View All Appointments',
          description: 'See appointments from all studio members'
        },
        {
          key: 'can_edit_all_appointments',
          label: 'Edit All Appointments',
          description: 'Modify appointments created by other members'
        }
      ]
    },
    {
      title: 'Inventory Management',
      icon: <Inventory sx={{ fontSize: 20 }} />,
      permissions: [
        {
          key: 'can_manage_inventory',
          label: 'Manage Studio Inventory',
          description: 'Access to studio-wide inventory management'
        },
        {
          key: 'can_view_inventory_reports',
          label: 'View Inventory Reports',
          description: 'Access inventory analytics and usage reports'
        }
      ]
    },
    {
      title: 'Custom Forms & Templates',
      icon: <Description sx={{ fontSize: 20 }} />,
      permissions: [
        {
          key: 'can_use_studio_forms',
          label: 'Use Studio Form Templates',
          description: 'Access to use studio owner\'s form templates for clients'
        },
        {
          key: 'can_view_studio_forms',
          label: 'View Studio Form Templates',
          description: 'See shared custom form templates from studio owner'
        },
        {
          key: 'can_edit_studio_forms',
          label: 'Edit Studio Form Templates',
          description: 'Modify shared custom form templates'
        },
        {
          key: 'can_delete_studio_forms',
          label: 'Delete Studio Form Templates',
          description: 'Remove shared form templates'
        },
        {
          key: 'can_view_all_form_submissions',
          label: 'View All Form Submissions',
          description: 'See form submissions from all studio members'
        }
      ]
    }
  ];

  const getPermissionLabel = (key) => {
    const permissionLabels = {
      'can_view_calendars': 'view calendars',
      'can_manage_appointments': 'manage appointments',
      'can_view_all_clients': 'view all clients',
      'can_edit_all_clients': 'edit all clients',
      'can_delete_all_clients': 'delete clients',
      'can_export_client_data': 'export client data',
      'can_view_all_appointments': 'view all appointments',
      'can_edit_all_appointments': 'edit all appointments',
      'can_manage_inventory': 'manage inventory',
      'can_view_inventory_reports': 'inventory reports',
      'can_use_studio_forms': 'use studio forms',
      'can_view_studio_forms': 'view studio forms',
      'can_edit_studio_forms': 'edit studio forms',
      'can_delete_studio_forms': 'delete studio forms',
      'can_view_all_form_submissions': 'view all submissions'
    };
    
    return permissionLabels[key] || key.replace('can_', '').replace(/_/g, ' ');
  };

  useEffect(() => {
    fetchStudioData();
  }, []);

  // ADDED: Fetch studio settings when tab changes to settings
  useEffect(() => {
    if (tabValue === 2 && studioData && isOwner) {
      fetchStudioSettings();
    }
  }, [tabValue, studioData]);

  const fetchStudioData = async () => {
    try {
      setLoading(true);
      const data = await api.fetchWithAuth('/studio/info/');
      
      if (data.studio) {
        setStudioData(data.studio);
        setMembers(data.members || []);
        
        // Find current user in members list
        const current = data.members?.find(member => member.is_current_user);
        setCurrentUser(current);
        
        // If this is a member-only view (not owner), show appropriate message
        if (data.studio.is_member_only) {
          setError('Studio Management is only available to studio owners. Contact your studio owner for management tasks.');
          return;
        }
      } else {
        // User doesn't have a studio yet and they're an owner, create one
        if (data.is_owner !== false) {
          await createStudio();
        } else {
          setError('You are not associated with any studio.');
        }
      }
    } catch (error) {
      console.error('Error fetching studio data:', error);
      setError('Failed to load studio information');
    } finally {
      setLoading(false);
    }
  };

  // ADDED: Fetch studio settings from shop profile
  const fetchStudioSettings = async () => {
    try {
      setSettingsLoading(true);
      const data = await api.fetchWithAuth('/shop/profile/');
      
      if (data) {
        setStudioSettings({
          name: data.name || '',
          phone: data.phone || '',
          email: data.email || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          zip_code: data.zip_code || '',
          client_ownership_policy: data.client_ownership_policy || 'studio'  // NEW
        });
        setSettingsChanged(false);
      }
    } catch (error) {
      console.error('Error fetching studio settings:', error);
      setError('Failed to load studio settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  // ADDED: Handle studio settings field changes
  const handleSettingsChange = (field, value) => {
    setStudioSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setSettingsChanged(true);
  };

  // ADDED: Save studio settings
  const saveStudioSettings = async () => {
    try {
      setSettingsLoading(true);
      setError(null);
      
      const response = await api.fetchWithAuth('/shop/profile/', {
        method: 'PUT',
        body: JSON.stringify(studioSettings)
      });
      
      if (response.success) {
        setSuccess('Studio settings saved successfully');
        setSettingsChanged(false);
        
        // If studio name was changed, refresh studio data to update the header
        if (response.studio_sync) {
          await fetchStudioData();
          setSuccess(`Studio settings saved successfully. ${response.studio_sync}`);
        }
      } else {
        throw new Error(response.error || 'Failed to save settings');
      }
      
    } catch (error) {
      setError(error.message || 'Failed to save studio settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const createStudio = async () => {
    try {
      const response = await api.fetchWithAuth('/studio/create/', {
        method: 'POST',
        body: JSON.stringify({
          name: "My Studio" // Default name, user can change later
        })
      });
      
      if (response.studio_id) {
        // Fetch the newly created studio data
        await fetchStudioData();
      }
    } catch (error) {
      console.error('Error creating studio:', error);
      setError('Failed to create studio');
    }
  };

  const handleInviteMember = async () => {
    try {
      setInviteLoading(true);
      setError(null);
      
      const response = await api.fetchWithAuth('/studio/invite/', {
        method: 'POST',
        body: JSON.stringify(inviteForm)
      });
      
      setSuccess(`Invitation sent to ${inviteForm.email}`);
      setInviteDialog(false);
      setInviteForm({ email: '', role: 'artist', first_name: '', last_name: '' });
      
      // Refresh data
      await fetchStudioData();
      
    } catch (error) {
      setError(error.message || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveMemberClick = (member) => {
    // NEW: Include policy info in confirmation message
    const policyOption = clientOwnershipOptions.find(opt => opt.value === studioSettings.client_ownership_policy);
    const policyMessage = policyOption 
      ? `\n\nClient Data Policy: "${policyOption.label}"\n${policyOption.description}`
      : '';
    
    setConfirmDialog({
      open: true,
      title: 'Remove Team Member',
      message: `Are you sure you want to remove ${member.first_name} ${member.last_name} from your studio? This action cannot be undone.${policyMessage}`,
      memberToRemove: member,
      onConfirm: () => handleRemoveMemberConfirmed(member.id)
    });
  };

  const handleRemoveMemberConfirmed = async (memberId) => {
    try {
      const response = await api.fetchWithAuth('/studio/remove-member/', {
        method: 'POST',
        body: JSON.stringify({ member_id: memberId })
      });
      
      // NEW: Show policy result in success message
      let successMessage = 'Member removed successfully';
      if (response.client_policy_applied && response.client_policy_applied !== 'none') {
        const policyOption = clientOwnershipOptions.find(opt => opt.value === response.client_policy_applied);
        if (policyOption) {
          successMessage = `Member removed successfully. Client policy "${policyOption.label}" was applied.`;
          if (response.clients_affected > 0) {
            successMessage += ` ${response.clients_affected} client(s) affected.`;
          }
        }
      }
      
      setSuccess(successMessage);
      await fetchStudioData();
      
    } catch (error) {
      setError('Failed to remove member');
    } finally {
      setConfirmDialog({
        open: false,
        title: '',
        message: '',
        onConfirm: null,
        memberToRemove: null
      });
    }
  };

  const handleToggleMemberStatus = async (member) => {
    try {
      const action = member.is_active ? 'deactivate' : 'activate';
      
      const response = await api.fetchWithAuth('/studio/members/toggle-status/', {
        method: 'POST',
        body: JSON.stringify({ member_id: member.id })
      });

      if (response.success) {
        setSuccess(response.message);
        await fetchStudioData();
      } else {
        throw new Error(response.error || 'Failed to update member status');
      }
    } catch (error) {
      setError(error.message || 'Failed to update member status');
    }
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      onConfirm: null,
      memberToRemove: null
    });
  };

  const handleAddSeats = async () => {
    try {
      setSeatsLoading(true);
      setError(null);
      
      const response = await api.fetchWithAuth('/studio/add-seats/', {
        method: 'POST',
        body: JSON.stringify({
          additional_seats: seatsToAdd
        })
      });
      
      setSuccess(`Added ${seatsToAdd} seats successfully`);
      setSeatsDialog(false);
      setSeatsToAdd(1);
      
      // Refresh data
      await fetchStudioData();
      
    } catch (error) {
      setError(error.message || 'Failed to add seats');
    } finally {
      setSeatsLoading(false);
    }
  };

  // Permission management functions (unchanged)
  const handleManagePermissions = (member) => {
    setSelectedMember(member);
    setPermissions(member.permissions || {});
    setPermissionDialog(true);
  };

  const handlePermissionChange = (permissionKey, value) => {
    setPermissions(prev => ({
      ...prev,
      [permissionKey]: value
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedMember) return;

    setPermissionLoading(true);
    try {
      const response = await api.fetchWithAuth('/studio/members/update-permissions/', {
        method: 'POST',
        body: JSON.stringify({
          member_id: selectedMember.id,
          permissions: permissions
        })
      });

      if (response.success) {
        setSuccess('Permissions updated successfully');
        
        setMembers(prevMembers => 
          prevMembers.map(member => 
            member.id === selectedMember.id 
              ? { ...member, permissions }
              : member
          )
        );
        
        setPermissionDialog(false);
      } else {
        throw new Error(response.error || 'Failed to update permissions');
      }
    } catch (err) {
      setError(err.message || 'Failed to update permissions');
    } finally {
      setPermissionLoading(false);
    }
  };

  const getStatusChip = (member) => {
    if (!member.is_active) {
      return (
        <Chip 
          label="Inactive"
          color="error"
          size="small"
          variant="outlined"
        />
      );
    }
    
    if (member.joined_at) {
      return (
        <Chip 
          label="Active"
          color="success"
          size="small"
          variant="outlined"
        />
      );
    } else {
      return (
        <Chip 
          label="Invited"
          color="warning"
          size="small"
          variant="outlined"
        />
      );
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      owner: 'primary',
      admin: 'secondary',
      artist: 'success',
      piercer: 'info',
      receptionist: 'warning',
      member: 'default'
    };
    return colors[role] || 'default';
  };

  const getSeatPrice = () => {
    return studioData?.subscription_plan === 'starter_studio' ? 25 : 40;
  };

  const isOwner = currentUser?.role === 'owner';

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // If user is not an owner, show access denied message
  if (studioData && !isOwner) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box textAlign="center" py={8}>
          <Security sx={{ fontSize: 80, color: 'text.secondary', mb: 3 }} />
          <Typography variant="h4" gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Studio Management
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
            Studio management features are only available to studio owners. 
            If you need to make changes to studio settings or manage team members, 
            please contact your studio owner.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Studio: {studioData.name} • Your Role: {currentUser?.role || 'Member'}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Studio Management
        </Typography>
        {studioData && (
          <Typography variant="subtitle1" color="textSecondary">
            {studioData.name} • {studioData.subscription_plan.replace('_', ' ').toUpperCase()}
          </Typography>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Overview" />
          <Tab label="Team Members" />
          <Tab label="Studio Settings" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Studio Overview Cards (unchanged) */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <People sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Team Size</Typography>
                </Box>
                <Typography variant="h3" color="primary">
                  {studioData?.used_seats || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  of {(studioData?.total_seats || 0) + (studioData?.additional_seats || 0)} total seats
                </Typography>
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSeatsDialog(true)}
                    disabled={studioData?.available_seats >= 5}
                  >
                    Add Seats
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Business sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h6">Available Seats</Typography>
                </Box>
                <Typography variant="h3" color="success.main">
                  {studioData?.available_seats || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ready for new team members
                </Typography>
                <Box mt={2}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PersonAdd />}
                    onClick={() => setInviteDialog(true)}
                    disabled={!studioData?.can_add_members}
                  >
                    Invite Member
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Settings sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="h6">Subscription</Typography>
                </Box>
                <Chip 
                  label={studioData?.subscription_plan?.replace('_', ' ').toUpperCase() || 'N/A'}
                  color="primary"
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="textSecondary">
                  Additional seats: ${getSeatPrice()}/month each
                </Typography>
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => window.open('/subscription-manager', '_blank')}
                  >
                    Manage Billing
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Activity
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Recent team member additions and changes will appear here.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Team Members tab (unchanged) */}
      {tabValue === 1 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              Team Members ({members.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => setInviteDialog(true)}
              disabled={!studioData?.can_add_members}
            >
              Invite Member
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      {member.first_name} {member.last_name}
                      {member.role === 'owner' && (
                        <Chip label="Owner" color="primary" size="small" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                        color={getRoleColor(member.role)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {getStatusChip(member)}
                    </TableCell>
                    <TableCell>
                      {member.joined_at 
                        ? new Date(member.joined_at).toLocaleDateString()
                        : 'Pending'
                      }
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1}>
                        {isOwner && member.role !== 'owner' && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleManagePermissions(member)}
                              color="primary"
                              title="Manage Permissions"
                            >
                              <Security />
                            </IconButton>
                            
                            <IconButton
                              size="small"
                              onClick={() => handleToggleMemberStatus(member)}
                              color={member.is_active ? "warning" : "success"}
                              title={member.is_active ? "Deactivate Member" : "Activate Member"}
                            >
                              {member.is_active ? <Block /> : <CheckCircle />}
                            </IconButton>
                            
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveMemberClick(member)}
                              color="error"
                              title="Remove Member"
                            >
                              <Delete />
                            </IconButton>
                          </>
                        )}
                        {member.role !== 'owner' && !isOwner && (
                          <Typography variant="caption" color="textSecondary">
                            Contact studio owner to manage
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {members.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                No team members yet. Invite your first team member to get started!
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* UPDATED: Studio Settings tab with client ownership policy */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          {/* Studio Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Studio Information
                  </Typography>
                  {settingsChanged && (
                    <Chip 
                      label="Unsaved Changes" 
                      color="warning" 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                </Box>
                
                {settingsLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      label="Studio Name"
                      value={studioSettings.name}
                      onChange={(e) => handleSettingsChange('name', e.target.value)}
                      margin="normal"
                      variant="outlined"
                      helperText="Changing this will update the studio name for all team members"
                    />
                    <TextField
                      fullWidth
                      label="Phone"
                      value={studioSettings.phone}
                      onChange={(e) => handleSettingsChange('phone', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                    <TextField
                      fullWidth
                      label="Email"
                      value={studioSettings.email}
                      onChange={(e) => handleSettingsChange('email', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                    <TextField
                      fullWidth
                      label="Address"
                      value={studioSettings.address}
                      onChange={(e) => handleSettingsChange('address', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="City"
                          value={studioSettings.city}
                          onChange={(e) => handleSettingsChange('city', e.target.value)}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          label="State"
                          value={studioSettings.state}
                          onChange={(e) => handleSettingsChange('state', e.target.value)}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          label="ZIP Code"
                          value={studioSettings.zip_code}
                          onChange={(e) => handleSettingsChange('zip_code', e.target.value)}
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                    
                    <Box mt={3}>
                      <Button 
                        variant="contained"
                        startIcon={<Save />}
                        onClick={saveStudioSettings}
                        disabled={!settingsChanged || settingsLoading}
                      >
                        {settingsLoading ? <CircularProgress size={24} /> : 'Save Changes'}
                      </Button>
                      {settingsChanged && (
                        <Button 
                          variant="outlined"
                          sx={{ ml: 2 }}
                          onClick={fetchStudioSettings}
                        >
                          Cancel
                        </Button>
                      )}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right column - Subscription + Client Ownership Policy */}
          <Grid item xs={12} md={6}>
            {/* Subscription Details */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Subscription Details
                </Typography>
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Current Plan
                  </Typography>
                  <Typography variant="h6">
                    {studioData?.subscription_plan?.replace('_', ' ').toUpperCase()}
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Total Seats
                  </Typography>
                  <Typography variant="h6">
                    {(studioData?.total_seats || 0) + (studioData?.additional_seats || 0)}
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Seat Pricing
                  </Typography>
                  <Typography variant="h6">
                    ${getSeatPrice()}/month per additional seat
                  </Typography>
                </Box>
                <Button variant="outlined" fullWidth onClick={() => window.location.href = '/subscription-manager'}>
                  Manage Subscription
                </Button>
              </CardContent>
            </Card>

            {/* NEW: Client Ownership Policy Card */}
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Policy sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">
                    Client Ownership Policy
                  </Typography>
                </Box>
                
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    This setting determines what happens to client data when a team member leaves your studio.
                  </Typography>
                </Alert>
                
                {settingsLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={studioSettings.client_ownership_policy}
                      onChange={(e) => handleSettingsChange('client_ownership_policy', e.target.value)}
                    >
                      {clientOwnershipOptions.map((option) => (
                        <Paper 
                          key={option.value} 
                          variant="outlined" 
                          sx={{ 
                            mb: 2, 
                            p: 2,
                            borderColor: studioSettings.client_ownership_policy === option.value ? 'primary.main' : 'divider',
                            bgcolor: studioSettings.client_ownership_policy === option.value ? 'action.selected' : 'background.paper'
                          }}
                        >
                          <FormControlLabel
                            value={option.value}
                            control={<Radio />}
                            label={
                              <Box>
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {option.label}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {option.description}
                                </Typography>
                              </Box>
                            }
                            sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                          />
                        </Paper>
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}
                
                <Box mt={2}>
                  <Button 
                    variant="contained"
                    startIcon={<Save />}
                    onClick={saveStudioSettings}
                    disabled={!settingsChanged || settingsLoading}
                    fullWidth
                  >
                    {settingsLoading ? <CircularProgress size={24} /> : 'Save Policy'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* All dialogs remain unchanged */}
      {/* Permission Management Dialog */}
      <Dialog 
        open={permissionDialog} 
        onClose={() => setPermissionDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { maxHeight: '80vh' }
        }}
      >
        <DialogTitle>
          Manage Permissions - {selectedMember?.first_name} {selectedMember?.last_name}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ mt: 1 }}>
            {permissionGroups.map((group) => (
              <Accordion key={group.title} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {group.icon}
                    <Typography variant="h6">{group.title}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {group.permissions.map((permission) => (
                      <ListItem key={permission.key} sx={{ px: 0 }}>
                        <ListItemText
                          primary={permission.label}
                          secondary={permission.description}
                        />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={permissions[permission.key] || false}
                            onChange={(e) => handlePermissionChange(permission.key, e.target.checked)}
                            color="primary"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSavePermissions}
            variant="contained"
            disabled={permissionLoading}
          >
            {permissionLoading ? <CircularProgress size={24} /> : 'Save Permissions'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Member Dialog */}
      <Dialog open={inviteDialog} onClose={() => setInviteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              margin="normal"
              required
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={inviteForm.first_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={inviteForm.last_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                  margin="normal"
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              select
              label="Role"
              value={inviteForm.role}
              onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
              margin="normal"
            >
              {roleOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleInviteMember}
            variant="contained"
            disabled={inviteLoading || !inviteForm.email}
          >
            {inviteLoading ? <CircularProgress size={24} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Seats Dialog */}
      <Dialog open={seatsDialog} onClose={() => setSeatsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Additional Seats</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" gutterBottom>
              Add more seats to accommodate additional team members.
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="Number of seats to add"
              value={seatsToAdd}
              onChange={(e) => setSeatsToAdd(Math.max(1, parseInt(e.target.value) || 1))}
              margin="normal"
              inputProps={{ min: 1, max: 20 }}
            />
            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2" color="textSecondary">
                Cost: {seatsToAdd} × ${getSeatPrice()}/month = ${seatsToAdd * getSeatPrice()}/month
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Billing starts immediately and is prorated.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeatsDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddSeats}
            variant="contained"
            disabled={seatsLoading}
          >
            {seatsLoading ? <CircularProgress size={24} /> : `Add ${seatsToAdd} Seat${seatsToAdd > 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog 
        open={confirmDialog.open} 
        onClose={handleConfirmDialogClose}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={confirmDialog.onConfirm}
            color="error"
            variant="contained"
          >
            Remove Member
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StudioManagement;